/**
 * Environment configuration. Per SASATH-0296, dink-api is reachable
 * same-origin at `/api/*`, rewritten onto dink-api's real context at the
 * edge — this app derives no API hostname. Every other platform host —
 * login, auth, brand, asset, pay — is a platform singleton anchored to the
 * tenant apex, not to this app's host, and cannot be derived; it is
 * bootstrapped once at app start (`src/config/bootstrap.ts`) and used
 * verbatim. DNKADM-0273 composed all four the same way and shipped hostnames
 * the platform never mints, routes, or certifies; DNKADM-0274 cut that to a
 * single derived host; DNKADM-0315 removes the last one. There are no URL
 * environment variables or URL defaults. The only build variable is the
 * deploy-environment stamp.
 *
 * `window.location` is the single source for the app origin and the API
 * base. Missing browser location data is a hard error because no safe
 * fallback can be derived.
 *
 * See `docs/design/dink-admin-application-design.md` for the full env
 * contract and why the other singleton hosts cannot be computed.
 */
type DeployEnv = 'staging' | 'production';

export interface AppEnv {
  /** Deployment environment for visible labels and deploy-specific behavior. */
  deployEnv: DeployEnv;
  /**
   * dink-api base — admin calls at `${base}/admin/...`. Bearer-only surface.
   * Same-origin: this console's own origin plus the fixed `/api` prefix,
   * rewritten onto dink-api's real context at the edge (SASATH-0296). No
   * hostname is derived.
   */
  journixApiBaseUrl: string;
  /** This console's own origin, derived from the browser at runtime. */
  appOrigin: string;
  /**
   * The member-app (guest) origin, when RECOVERABLE from this console's own
   * host (DNKADM-0283) — the guest-booking preview's target. Not a service
   * origin and not a composition: the platform provisions this console AT
   * `admin.<memberHost>` (e.g. `admin.simcreek.staging.…` beside member
   * `simcreek.staging.…`), so stripping the one leading `admin.` label merely
   * inverts the rule that minted this host — the result is a host the
   * platform already serves, unlike the DNKADM-0273 label-composed hosts the
   * 0274 contract forbids. On a host with no leading `admin.` label (local
   * dev, a vanity admin domain with no textual pair) the inversion does not
   * apply and this is `null` — consumers hide the preview affordance; there
   * is never a guessed or configured fallback host.
   */
  memberOrigin: string | null;
}

/**
 * The same-origin API prefix, rewritten onto dink-api's real context at the
 * edge (SASATH-0296). A product constant, identical in every environment —
 * never a build variable.
 */
const API_PREFIX = '/api';

/**
 * The browser location. There is NO fallback: without it the api origin
 * cannot be derived, and a placeholder would silently produce a wrong origin
 * (DNKADM-0273). Failing here is the intended behavior.
 */
function requireBrowserLocation(): { protocol: string; host: string; origin: string } {
  const loc = typeof window !== 'undefined' ? window.location : undefined;
  if (!loc?.origin || !loc.host || !loc.protocol) {
    throw new Error('Cannot derive the API origin: window.location is unavailable.');
  }
  return { protocol: loc.protocol, host: loc.host, origin: loc.origin };
}

function normalizeDeployEnv(value: string | undefined): DeployEnv | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'staging' || normalized === 'production') {
    return normalized;
  }
  return null;
}

function hostHasStagingLabel(value: string): boolean {
  try {
    return new URL(value).hostname.split('.').includes('staging');
  } catch {
    return false;
  }
}

function inferDeployEnv(appOrigin: string): DeployEnv {
  if (hostHasStagingLabel(appOrigin)) {
    return 'staging';
  }
  return 'production';
}

const browserLocation = requireBrowserLocation();

/**
 * Same-origin: this console's own origin plus the fixed `/api` prefix. No
 * hostname is derived — `/dink-api` is an edge concern the browser never
 * sees (SASATH-0296).
 */
const journixApiBaseUrl = `${browserLocation.origin}${API_PREFIX}`;

/**
 * Recover the member origin by inverting the `admin.` prefix that minted this
 * console's host (see {@link AppEnv.memberOrigin}); `null` when the host does
 * not carry the label (nothing is guessed).
 */
function deriveMemberOrigin(protocol: string, host: string): string | null {
  const label = 'admin.';
  if (!host.toLowerCase().startsWith(label)) return null;
  const memberHost = host.slice(label.length);
  if (memberHost.length === 0) return null;
  return `${protocol}//${memberHost}`;
}
const deployEnv =
  normalizeDeployEnv(import.meta.env.VITE_DEPLOY_ENV) ?? inferDeployEnv(browserLocation.origin);

export const env: AppEnv = {
  deployEnv,
  journixApiBaseUrl,
  appOrigin: browserLocation.origin,
  memberOrigin: deriveMemberOrigin(browserLocation.protocol, browserLocation.host),
};
